/**
 * Settings Store & Sensitivity Calibration for Aim Assist Training Lab
 * 100% Client-side local persistence with standard cm/360 physics conversions
 */

import { GAME_PRESETS } from './constants.js';

const STORAGE_KEY = 'kazakan_aim_settings_v1';

export const DEFAULT_SETTINGS = {
  // Game & Sensitivity Calibration
  selectedGame: 'valorant',
  sens: 0.35,
  dpi: 800,
  fov: 103,
  invertY: false,
  adsMultiplier: 0.85,
  
  // Crosshair Customization
  crosshairStyle: 'cross',
  crosshairColor: '#38bdf8',
  crosshairSize: 10,
  crosshairThickness: 2,
  crosshairGap: 4,
  dynamicBloom: true,
  followRecoil: true,

  // Target Aesthetics & Motion
  targetColor: 'cyan',
  highContrast: false,
  targetShape: 'mixed',
  targetRangeZone: 'all', // 'all' | 'close' | 'mid' | 'long'
  motionPattern: 'auto',
  targetSpeed: 1.0,
  targetSize: 1.0,
  movingTargetsEnabled: true,

  // Weapon & Arena Mechanics
  selectedWeapon: 'rifle', // 'rifle' | 'smg' | 'sniper' | 'pistol'
  arenaTheme: 'cyber',
  bulletTracers: true,
  impactSparks: true,
  hitMarkers: true,
  floatingDamage: true,

  // Audio & Systems
  soundEnabled: true,
  soundVolume: 0.7,
  adaptiveDifficulty: true,
  controlMode: 'desktop', // 'desktop' | 'touch'
  touchSensitivity: 1.2,
  touchSensY: 1.2,
  gyroEnabled: false,
  gyroSensitivity: 1.0
};

export class SettingsStore {
  constructor() {
    this.state = { ...DEFAULT_SETTINGS };
    this.listeners = new Set();
    this.load();
  }

  load() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.state = { ...DEFAULT_SETTINGS, ...parsed };
      }
    } catch {
      this.state = { ...DEFAULT_SETTINGS };
    }
  }

  save() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      // LocalStorage write error fallback
    }
    this.notify();
  }

  get(key) {
    return this.state[key];
  }

  set(key, value) {
    this.state[key] = value;
    this.save();
  }

  update(patch) {
    this.state = { ...this.state, ...patch };
    this.save();
  }

  reset() {
    this.state = { ...DEFAULT_SETTINGS };
    this.save();
  }

  subscribe(listener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  notify() {
    for (const listener of this.listeners) {
      try {
        listener(this.state);
      } catch (err) {
        console.error('SettingsStore listener error:', err);
      }
    }
  }

  /**
   * Calculates the exact cm/360 distance required for one complete 360-degree rotation.
   * Standard formula: cm/360 = (360 / (sensitivity * yaw)) * 2.54 / DPI
   */
  calculateCm360() {
    const preset = GAME_PRESETS[this.state.selectedGame] || GAME_PRESETS.custom;
    const yaw = preset.yaw;
    const sens = Number(this.state.sens) || 1.0;
    const dpi = Number(this.state.dpi) || 800;

    if (sens <= 0 || dpi <= 0 || yaw <= 0) return 0;
    const inchesPerRev = 360 / (sens * yaw * dpi);
    const cmPerRev = inchesPerRev * 2.54;
    return Number.isFinite(cmPerRev) ? Math.round(cmPerRev * 10) / 10 : 0;
  }

  /**
   * Converts sensitivity from current game preset to another game preset based on yaw equivalence.
   */
  convertSensitivity(targetGameId) {
    const sourcePreset = GAME_PRESETS[this.state.selectedGame] || GAME_PRESETS.custom;
    const targetPreset = GAME_PRESETS[targetGameId] || GAME_PRESETS.custom;

    const sourceYaw = sourcePreset.yaw;
    const targetYaw = targetPreset.yaw;
    const currentSens = Number(this.state.sens) || 1.0;

    // sourceYaw * currentSens = targetYaw * targetSens
    const targetSens = (currentSens * sourceYaw) / targetYaw;
    return Math.round(targetSens * 1000) / 1000;
  }
}
